import { useCallback, useEffect, useRef, useState } from 'react'
import OrderDocuments from './OrderDocuments'
import OrderInfo from './OrderInfo'
import { confidenceType, lineItemType, orderType, shipToType } from '../types'
import { v4 } from 'uuid'
import SubmissionSuccess from './SubmissionSuccess'
import { tempFiles } from './Correction'
import axios from 'axios'
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Snackbar,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from '@mui/material'
import './NewOrder.css'
import { useBlocker } from 'react-router'
import LineItemReview from './LineItemReview'
import axiosRetry, { IAxiosRetryConfig, isNetworkOrIdempotentRequestError } from 'axios-retry'
import { createComprehensiveErrorReport } from '../networkMonitoring'
import { datadogRum } from '@datadog/browser-rum'
import { useClassList, useUser } from '../hooks'
import { flattenLineItems } from '../EditLines/EditLinesHelper'

export type fileInputType = {
  impresquote: File[]
  governmentaward: File[]
  vendorquote: File[]
  miscellaneous: File[]
}

function NewOrder({
  originalOrder = null,
  originalFiles = null,
  originalLines = null,
  originalAddresses = null,
}: {
  originalOrder?: orderType | null
  originalFiles?: tempFiles | null
  originalLines?: lineItemType[] | null
  originalAddresses?: (shipToType & { default: boolean; id: number })[] | null
}) {
  const user = useUser()
  const classList = useClassList()

  const [activeStep, setActiveStep] = useState(0)
  const blocker = useBlocker(activeStep !== 3)

  const [files, setFiles] = useState<fileInputType>({
    impresquote: [],
    governmentaward: [],
    vendorquote: [],
    miscellaneous: [],
  })
  const [draftSaveState, setDraftSaveState] = useState<'saving' | 'error' | 'saved' | ''>('')
  const [uploadError, setUploadError] = useState<string[]>([])
  const [sharepointLink, setSharepointLink] = useState<string[]>([])
  const [order, setOrder] = useState<orderType>({ OrderTotal: null, TotalCost: null, Urgent: false } as orderType)
  const [leavingDialogOpen, setLeavingDialogOpen] = useState<'reload' | null>(null)
  const [contraqerLineItems, setContraqerLineItems] = useState<lineItemType[]>(originalLines ?? [])
  const [submitLoading, setSubmitLoading] = useState<'loading' | 'error' | ''>('')
  const [contraqerBackup, setContraqerBackup] = useState<Date | null>(null)
  const [lineItemsLoading, setLineItemsLoading] = useState<'loading' | 'error' | ''>('')
  const [shippingAddresses, setShippingAddresses] = useState<(shipToType & { default: boolean; id: number })[]>(
    originalAddresses
      ? Array.from(originalAddresses)
      : [
          {
            ShipToAttention: null,
            ShipToCompany: null,
            ShipToAddress1: null,
            ShipToAddress2: null,
            ShipToAddressCity: null,
            ShipToAddressState: null,
            ShipToAddressZip: null,
            ShipToAddressCountry: null,
            ReceiverContactName: null,
            ReceiverContactEmail: null,
            ReceiverContactPhone: null,
            default: true,
            id: 0,
          },
        ],
  )

  const orderConf = useRef<confidenceType>({
    QuoteNumber: null,
    PurchaseVehicle: null,
    OrderTotal: null,
    QuoteDate: null,
    QuoteExpires: null,
    CustomerPO: null,
    GovPaymentTerms: null,
    VendorPaymentTerms: null,
    DeliveryDueDate: null,
    AwardDate: null,
    TotalCost: null,
    ShippingCharges: null,
  } as confidenceType)
  const confChanges = useRef<confidenceType>({} as confidenceType)

  const reloadAlert = (e: BeforeUnloadEvent) => {
    e.preventDefault()
    setLeavingDialogOpen('reload')
  }

  const getContraqerLineItems = useCallback(() => {
    return new Promise<void>((resolve, reject) => {
      axios
        .get(`/contraqer/lineitems/${order.QuoteNumber}`)
        .then(res => {
          setContraqerLineItems(
            res.data
              .sort((a: lineItemType, b: lineItemType) => (a.Number ?? 0) - (b.Number ?? 0))
              .map((item: lineItemType) => ({
                ...item,
                ShipToAttention: null,
                ShipToCompany: null,
                ShipToAddress1: null,
                ShipToAddress2: null,
                ShipToAddressCity: null,
                ShipToAddressState: null,
                ShipToAddressZip: null,
                ShipToAddressCountry: null,
                ReceiverContactName: null,
                ReceiverContactEmail: null,
                ReceiverContactPhone: null,
                Class: order.ContractNumber,
                children: item.children?.map(child => ({
                  ...child,
                  Class: order.ContractNumber,
                })),
              })),
          )
          resolve()
        })
        .catch(err => {
          reject(err)
        })
    })
  }, [classList, order.PurchaseVehicle, order.QuoteNumber, setActiveStep, setContraqerBackup, setContraqerLineItems])

  const saveDraft = () => {
    setDraftSaveState('saving')
    axios
      .post('/submitOrder', {
        order: {
          ...order,
          ReceiverContactPhone: order.ReceiverContactPhone ? order.ReceiverContactPhone.trim() : null,
          LicenseContactPhone: order.LicenseContactPhone ? order.LicenseContactPhone.trim() : null,
          Status: 'Draft',
          CreatedBy: user.email,
          SubmissionTimestamp: new Date(),
        },
        confidence: confChanges.current,
        lineItems: flattenLineItems(contraqerLineItems).map(({ children: _c, ...rest }) => rest),
      })
      .then(async () => {
        await axios.delete(`/deleteDraftFiles/${order.Id}`).catch(err => {
          console.error(err)
        })
        const form = new FormData()
        for (const fileList of [files.impresquote, files.governmentaward, files.vendorquote, files.miscellaneous]) {
          for (let i = 0; i < fileList.length; i++) {
            const prefix =
              fileList === files.impresquote
                ? 'IMPRESQuote'
                : fileList === files.governmentaward
                ? 'GovernmentAward'
                : fileList === files.vendorquote
                ? 'VendorQuote'
                : 'RelatedDoc'
            form.append('file', fileList[i], prefix + '_' + fileList[i].name)
          }
        }
        axios
          .put(`/upload/${order.Id}`, form)
          .then(() => {
            setDraftSaveState('saved')
          })
          .catch(err => {
            setDraftSaveState('error')
            if (err.response) {
              // received error code other than 2xx
              console.error(err.response)
            } else if (err.request) {
              // no response received
              console.error(err.request)
            } else {
              // request was not made
              console.error(err.message)
            }
            axios.post('/log', { message: err })
          })
      })
      .catch(err => {
        setDraftSaveState('error')
        if (err.response) {
          // received error code other than 2xx
          console.error(err.response)
        } else if (err.request) {
          // no response received
          console.error(err.request)
        } else {
          // request was not made
          console.error(err.message)
        }
      })
  }

  const axiosRetryConfig: IAxiosRetryConfig = {
    retries: 3,
    retryDelay: axiosRetry.exponentialDelay,
    retryCondition: error => {
      return isNetworkOrIdempotentRequestError(error) || axiosRetry.isRetryableError(error)
    },
  }

  const handleSubmit = () => {
    setSubmitLoading('loading')
    axiosRetry(axios, axiosRetryConfig)
    axios
      .post('/submitOrder', {
        order: {
          ...order,
          ReceiverContactPhone: order.ReceiverContactPhone ? order.ReceiverContactPhone.trim() : null,
          LicenseContactPhone: order.LicenseContactPhone ? order.LicenseContactPhone.trim() : null,
          Status: 'Submitted',
          SubmissionTimestamp: new Date(),
          CreatedBy: user.email,
        },
        confidence: confChanges.current,
        lineItems: flattenLineItems(contraqerLineItems).map(({ children: _c, ...rest }) => rest),
        originalOrder: originalOrder?.Status === 'Rejected' ? originalOrder.Id : null,
      })
      .then(async res => {
        for (const fileList of [files.impresquote, files.governmentaward, files.vendorquote, files.miscellaneous]) {
          for (let i = 0; i < fileList.length; i++) {
            const form = new FormData()
            const prefix =
              fileList === files.impresquote
                ? 'IMPRESQuote'
                : fileList === files.governmentaward
                ? 'GovernmentAward'
                : fileList === files.vendorquote
                ? 'VendorQuote'
                : 'RelatedDoc'
            form.append('file', fileList[i], prefix + '_' + fileList[i].name)
            try {
              await axios.put(`/upload/${order.Id}`, form, {
                timeout: 2 * 60 * 1000,
              })
              const context = createComprehensiveErrorReport(fileList[i])
              datadogRum.addAction('file upload success', context)
            } catch (err) {
              setUploadError(arr => [...arr, prefix + '_' + fileList[i].name])
              setSharepointLink(arr => [...arr, `${prefix}_${res.data[prefix]}`])

              const context = createComprehensiveErrorReport(fileList[i])
              datadogRum.addError(err, context)
            }
          }
        }
        setActiveStep(3)
        setSubmitLoading('')
      })
      .catch(err => {
        setSubmitLoading('error')
        if (err.response) {
          // received error code other than 2xx
          console.error(err.response)
        } else if (err.request) {
          // no response received
          console.error(err.request)
        } else {
          // request was not made
          console.error(err.message)
        }
      })
  }

  useEffect(() => {
    if (originalOrder) {
      setOrder(structuredClone(originalOrder))
    }

    if (originalOrder?.Status !== 'Draft') {
      setOrder(prev => ({ ...prev, Id: v4() }))
    }

    if (originalOrder?.Status === 'Rejected') {
      document.title = 'Correction | Order Portal'
    } else if (originalOrder?.Status === 'Draft') {
      document.title = 'Continue Draft | Order Portal'
    } else {
      document.title = 'New Order | Order Portal'
    }

    window.addEventListener('beforeunload', reloadAlert)
    return () => {
      window.removeEventListener('beforeunload', reloadAlert)
    }
  }, [])

  return (
    <div className="new-order">
      <div className="new-order-header">
        <Typography variant="h6">
          {originalOrder?.Status === 'Rejected'
            ? 'Correction'
            : originalOrder?.Status === 'Draft'
            ? 'Continue Draft'
            : 'New Order'}
        </Typography>
        <Stepper activeStep={activeStep}>
          <Step>
            <StepLabel>Order Type and Documents</StepLabel>
          </Step>
          <Step>
            <StepLabel>Contact and Order Information</StepLabel>
          </Step>
          <Step>
            <StepLabel>Line Item Review</StepLabel>
          </Step>
        </Stepper>
        <Button
          variant="outlined"
          loading={draftSaveState === 'saving'}
          onClick={saveDraft}
          disabled={activeStep === 3}
        >
          Save Draft
        </Button>
        <Snackbar
          open={draftSaveState === 'saved'}
          autoHideDuration={5000}
          onClose={() => setDraftSaveState('')}
          message="Draft saved successfully"
        />
        <Snackbar open={draftSaveState === 'error'}>
          <Alert severity="error" variant="filled" onClose={() => setDraftSaveState('')}>
            An error occurred while saving the draft. Please try again or contact support if the problem persists.
          </Alert>
        </Snackbar>
      </div>
      <Divider flexItem />
      {activeStep === 3 ? (
        <>
          <SubmissionSuccess order={order} uploadError={uploadError} sharepointLink={sharepointLink} />
        </>
      ) : (
        <div className="new-order-content" style={{ width: activeStep === 2 ? '100%' : '80%' }}>
          {activeStep === 0 && (
            <OrderDocuments
              setView={setActiveStep}
              order={order}
              setOrder={setOrder}
              files={files}
              setFiles={setFiles}
              originalFiles={originalFiles}
              orderConf={orderConf}
              isCorrection={originalOrder?.Status === 'Rejected' || originalOrder?.Status === 'Draft'}
              confChanges={confChanges}
            />
          )}
          {activeStep === 1 && (
            <OrderInfo
              setView={setActiveStep}
              order={order}
              originalQuote={originalOrder?.QuoteNumber ?? null}
              setOrder={setOrder}
              orderConf={orderConf}
              shippingAddresses={shippingAddresses}
              lineItems={contraqerLineItems}
              isCorrection={originalOrder?.Status === 'Rejected' || originalOrder?.Status === 'Draft'}
              confChanges={confChanges}
              setLineItems={setContraqerLineItems}
              setShippingAddresses={setShippingAddresses}
              setContraqerBackup={setContraqerBackup}
              getContraqerLineItems={getContraqerLineItems}
              lineItemsLoading={lineItemsLoading}
              setLineItemsLoading={setLineItemsLoading}
            />
          )}
          {activeStep === 2 && (
            <LineItemReview
              headerPurchaseVehicle={order.PurchaseVehicle ?? ''}
              isCorrection={originalOrder?.Status === 'Rejected' || originalOrder?.Status === 'Draft'}
              lineItems={contraqerLineItems}
              setLineItemsLoading={setLineItemsLoading}
              setLineItems={setContraqerLineItems}
              setView={setActiveStep}
              submitOrder={handleSubmit}
              submitLoading={submitLoading === 'loading'}
              shippingAddresses={shippingAddresses}
              cqBackup={contraqerBackup}
              setCqBackup={setContraqerBackup}
              getContraqerLineItems={getContraqerLineItems}
              lineItemsLoading={lineItemsLoading}
            />
          )}
          <Snackbar open={submitLoading === 'error'}>
            <Alert severity="error" variant="filled" onClose={() => setSubmitLoading('')}>
              An error occurred while submitting the order. Please try again or contact support if the problem persists.
            </Alert>
          </Snackbar>
          <Dialog
            fullWidth
            open={leavingDialogOpen === 'reload' || blocker.state === 'blocked'}
            onClose={() => setLeavingDialogOpen(null)}
          >
            <DialogTitle>Leaving page</DialogTitle>
            <DialogContent>
              <Typography>Are you sure you want to leave? Changes you made will not be saved.</Typography>
            </DialogContent>
            <DialogActions>
              <Button
                variant="outlined"
                onClick={() => {
                  setLeavingDialogOpen(null)
                  blocker.reset?.()
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={() => {
                  setLeavingDialogOpen(null)
                  blocker.proceed?.()
                }}
              >
                Leave
              </Button>
            </DialogActions>
          </Dialog>
        </div>
      )}
    </div>
  )
}

export default NewOrder
